import type { AgingReport } from '@erp/shared';
import Decimal from 'decimal.js';
import { AlertTriangle, Coins, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  Input,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/shadcn/index.js';
import { EmptyState, FormField } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { formatMoney } from '../../lib/money.js';

import { KpiCard } from './_shared/KpiCard.js';

interface AgingPageProps {
  /** Report endpoint under /api/v1/reports, e.g. "ar-aging" or "ap-aging". */
  endpoint: string;
  /** Translation key for title/description ("arAging" or "apAging"). */
  ns: string;
}

export function AgingPage({ endpoint, ns }: AgingPageProps): JSX.Element {
  const { t, i18n } = useTranslation('reports');
  const { t: tc } = useTranslation();
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  // The owner's per-branch lens: '' = whole company.
  const [warehouseId, setWarehouseId] = useState('');
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [report, setReport] = useState<AgingReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let on = true;
    void api().get<{ items: Array<{ id: string; name: string; isActive: boolean }> }>('/warehouses')
      .then((r) => { if (on) setWarehouses(r.data.items.filter((w) => w.isActive)); })
      .catch(() => undefined);
    return () => { on = false; };
  }, []);

  useEffect(() => {
    setError(null);
    setReport(null);
    void api()
      .get<AgingReport>(`/reports/${endpoint}?asOf=${asOf}${warehouseId ? `&warehouseId=${warehouseId}` : ''}`)
      .then((r) => setReport(r.data))
      .catch((e) => setError(String((e as Error).message ?? e)));
  }, [asOf, endpoint, warehouseId]);

  const fmt = (amt: string, currency: string): string =>
    formatMoney(amt, { locale: i18n.language, currency });

  const stats = useMemo(() => {
    if (!report) return null;
    // KPIs are single figures, so they can only mean one currency. With one
    // currency present, use it; with several, fall back to the base-currency
    // slice (summing across currencies would be meaningless — see the table
    // below for the full per-currency breakdown).
    const kpiCurrency = report.totalsByCurrency.length === 1
      ? report.totalsByCurrency[0]!.currencyCode
      : report.baseCurrencyCode;
    const slice = report.totalsByCurrency.find((x) => x.currencyCode === kpiCurrency) ?? null;
    const total = new Decimal(slice?.total ?? 0);
    const overdue = new Decimal(slice?.days_1_30 ?? 0)
      .plus(slice?.days_31_60 ?? 0)
      .plus(slice?.days_61_90 ?? 0)
      .plus(slice?.days_90_plus ?? 0);
    const overduePct = total.gt(0)
      ? overdue.dividedBy(total).times(100).toFixed(1)
      : '0.0';
    const top = report.rows.length === 0
      ? null
      : [...report.rows]
          .map((r) => ({ ...r, totalN: new Decimal(r.total) }))
          .sort((a, b) => b.totalN.comparedTo(a.totalN))[0];
    // Rows are per party+currency now — count distinct parties.
    const partyCount = new Set(report.rows.map((r) => r.partyId)).size;
    return { kpiCurrency, total, overdue, overduePct, top, partyCount };
  }, [report]);

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t(`${ns}.title`)}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t(`${ns}.description`)}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {warehouses.length > 1 && (
            <FormField id="ag-branch" label={tc('filters.branch', { defaultValue: 'Branch' })}>
              <select
                id="ag-branch"
                value={warehouseId}
                onChange={(e) => setWarehouseId(e.target.value)}
                className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand"
              >
                <option value="">{tc('filters.allBranches', { defaultValue: 'All branches' })}</option>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </FormField>
          )}
          <FormField id="ag-asof" label={t('aging.asOf')}>
            <Input id="ag-asof" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          </FormField>
        </div>
      </header>

      {error ? (
        <Card className="border-danger/40 bg-danger/5 p-4">
          <div className="flex items-start gap-2 text-sm text-danger" role="alert">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
            <div className="flex-1">
              <p className="font-medium">{t('aging.loadFailed')}</p>
              <p className="mt-0.5 text-xs text-danger/80">{error}</p>
            </div>
          </div>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <KpiCard
              tone="info"
              icon={Coins}
              label={t('aging.outstanding')}
              value={stats ? fmt(stats.total.toFixed(4), stats.kpiCurrency) : '—'}
              loading={!report}
            />
            <KpiCard
              tone="danger"
              icon={AlertTriangle}
              label={t('aging.overdue')}
              value={stats ? fmt(stats.overdue.toFixed(4), stats.kpiCurrency) : '—'}
              hint={stats ? `${stats.overduePct}%` : undefined}
              loading={!report}
            />
            <KpiCard
              tone="brand"
              icon={Users}
              label={t('aging.columns.party')}
              value={stats ? String(stats.partyCount) : '—'}
              loading={!report}
            />
            <KpiCard
              tone="warning"
              icon={Coins}
              label={t('aging.topParty')}
              value={stats?.top ? fmt(stats.top.total, stats.top.currencyCode) : '—'}
              hint={stats?.top ? stats.top.partyName : undefined}
              loading={!report}
            />
          </div>

          {!report ? (
            <Card className="p-6"><Skeleton className="h-64 w-full" /></Card>
          ) : report.rows.length === 0 ? (
            <EmptyState
              icon={<Coins className="h-8 w-8" aria-hidden />}
              title={t('aging.empty')}
              description={t('aging.emptyDescription')}
            />
          ) : (
        <Card className="overflow-hidden p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('aging.columns.party')}</TableHead>
                <TableHead>{t('aging.columns.currency', { defaultValue: 'Currency' })}</TableHead>
                <TableHead className="text-end">{t('aging.columns.current')}</TableHead>
                <TableHead className="text-end">{t('aging.columns.days_1_30')}</TableHead>
                <TableHead className="text-end">{t('aging.columns.days_31_60')}</TableHead>
                <TableHead className="text-end">{t('aging.columns.days_61_90')}</TableHead>
                <TableHead className="text-end">{t('aging.columns.days_90_plus')}</TableHead>
                <TableHead className="text-end font-semibold">{t('aging.columns.total')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.rows.map((row) => {
                const total = new Decimal(row.total);
                const overdue = new Decimal(row.days_1_30)
                  .plus(row.days_31_60).plus(row.days_61_90).plus(row.days_90_plus);
                const overduePct = total.gt(0) ? overdue.dividedBy(total).times(100).toNumber() : 0;
                return (
                  <TableRow key={`${row.partyId}-${row.currencyCode}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="min-w-0">
                          <div className="truncate font-medium">{row.partyName}</div>
                          <div className="font-mono text-xs text-fg-muted">{row.partyCode}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-fg-muted">{row.currencyCode}</TableCell>
                    <TableCell className="text-end font-mono tabular-nums">{fmt(row.current, row.currencyCode)}</TableCell>
                    <TableCell className={`text-end font-mono tabular-nums ${overdueCellTone(1)}`}>
                      {fmt(row.days_1_30, row.currencyCode)}
                    </TableCell>
                    <TableCell className={`text-end font-mono tabular-nums ${overdueCellTone(2)}`}>
                      {fmt(row.days_31_60, row.currencyCode)}
                    </TableCell>
                    <TableCell className={`text-end font-mono tabular-nums ${overdueCellTone(3)}`}>
                      {fmt(row.days_61_90, row.currencyCode)}
                    </TableCell>
                    <TableCell className={`text-end font-mono tabular-nums ${overdueCellTone(4)}`}>
                      {fmt(row.days_90_plus, row.currencyCode)}
                    </TableCell>
                    <TableCell className="text-end">
                      <div className="font-mono font-semibold tabular-nums">{fmt(row.total, row.currencyCode)}</div>
                      {overduePct > 0 && (
                        <div className="mt-1 h-1 w-20 overflow-hidden rounded-full bg-bg-subtle ms-auto">
                          <div
                            className="h-full rounded-full bg-danger/70"
                            style={{ width: `${Math.min(100, overduePct).toFixed(1)}%` }}
                            aria-hidden
                          />
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {/* Per-currency totals — one strip per currency (never summed across). */}
              {report.totalsByCurrency.map((grp) => (
                <TableRow key={grp.currencyCode} className="border-t-2 border-border bg-bg-subtle/60 hover:bg-bg-subtle/60">
                  <TableCell className="font-semibold">{t('aging.total')}</TableCell>
                  <TableCell className="font-mono text-xs font-semibold">{grp.currencyCode}</TableCell>
                  <TableCell className="text-end font-mono font-semibold tabular-nums">
                    {fmt(grp.current, grp.currencyCode)}
                  </TableCell>
                  <TableCell className="text-end font-mono font-semibold tabular-nums">
                    {fmt(grp.days_1_30, grp.currencyCode)}
                  </TableCell>
                  <TableCell className="text-end font-mono font-semibold tabular-nums">
                    {fmt(grp.days_31_60, grp.currencyCode)}
                  </TableCell>
                  <TableCell className="text-end font-mono font-semibold tabular-nums">
                    {fmt(grp.days_61_90, grp.currencyCode)}
                  </TableCell>
                  <TableCell className="text-end font-mono font-semibold tabular-nums">
                    {fmt(grp.days_90_plus, grp.currencyCode)}
                  </TableCell>
                  <TableCell className="text-end font-mono text-base font-semibold tabular-nums">
                    {fmt(grp.total, grp.currencyCode)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
          )}
        </>
      )}
    </div>
  );
}

function overdueCellTone(bucket: 1 | 2 | 3 | 4): string {
  // Visual escalation: 1-30 stays neutral, 31-60 warns, 61-90 darker warn,
  // 90+ flags as danger. Keeps the eye drawn to the worst columns at a glance.
  switch (bucket) {
    case 1: return '';
    case 2: return 'text-warning';
    case 3: return 'text-warning font-medium';
    case 4: return 'text-danger font-medium';
  }
}
